import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import {
  RRP_AUTO_BIND_PREF_KEY,
  makeComplianceAutoBindRuleId,
  normalizeRrpAutoBindRule,
  readComplianceAutoBindRules,
  saveComplianceAutoBindRules,
} from '@/shein-platform/modules/compliance/complianceAutoBindRules';
import { showToast } from '@/utils/toast';

/**
 * 建议零售价自动规则：按类目选择参与范围；执行时取该类目下已开启自动规则的建议价资料库，套价并提交审核。
 */
export default function RrpAutoRuleModal({
  open,
  shopId,
  busy = false,
  onClose,
  onRan,
}) {
  const { t } = useTranslation('workbench');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [categories, setCategories] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftCategoryLabel, setDraftCategoryLabel] = useState('');
  const [draftProductTypeId, setDraftProductTypeId] = useState('');
  const [draftEnabled, setDraftEnabled] = useState(true);

  const leafOptions = useMemo(() => flattenLeafCategories(categories), [categories]);

  useEffect(() => {
    if (!open || !shopId) {
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setLoading(true);
    Promise.all([
      sheinPlatformAPI.getShopCategories(shopId).catch(() => null),
      sheinPlatformAPI.getPreferences().catch(() => null),
    ])
      .then(([catRes, prefRes]) => {
        if (cancelled) return;
        setCategories(catRes?.categories ?? catRes?.items ?? catRes?.list ?? catRes ?? []);
        const prefs = prefRes?.preferences || prefRes || {};
        setRules(readComplianceAutoBindRules(prefs, RRP_AUTO_BIND_PREF_KEY, shopId, normalizeRrpAutoBindRule));
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [open, shopId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !running && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, running, busy, onClose]);

  const onCategoryChange = (value) => {
    const id = String(value || '').trim();
    setDraftCategoryId(id);
    if (id === '*') {
      setDraftCategoryLabel('全部类目');
      setDraftProductTypeId('');
      return;
    }
    const opt = leafOptions.find((o) => String(o.categoryId) === id);
    setDraftCategoryLabel(opt?.label || '');
    setDraftProductTypeId(opt?.productTypeId || '');
  };

  const addRule = () => {
    if (!draftCategoryId) {
      showToast(t('rrpLib.needCategory'), 'warning');
      return;
    }
    if (rules.some((r) => String(r.categoryId) === String(draftCategoryId))) {
      showToast(t('rrpOps.ruleExists'), 'warning');
      return;
    }
    const next = normalizeRrpAutoBindRule({
      id: makeComplianceAutoBindRuleId('rrp'),
      enabled: draftEnabled,
      categoryId: draftCategoryId,
      categoryPathLabel: draftCategoryLabel || (draftCategoryId === '*' ? '全部类目' : draftCategoryId),
      productTypeId: draftProductTypeId,
    });
    if (!next) return;
    setRules((prev) => [...prev, next]);
    setDraftCategoryId('');
    setDraftCategoryLabel('');
    setDraftProductTypeId('');
  };

  const toggleRule = (id, enabled) => {
    setRules((prev) => prev.map((rule) => (rule.id === id ? { ...rule, enabled } : rule)));
  };

  const removeRule = (id) => {
    setRules((prev) => prev.filter((rule) => rule.id !== id));
  };

  const persistRules = () => saveComplianceAutoBindRules({
    getPreferences: () => sheinPlatformAPI.getPreferences(),
    updatePreferences: (body) => sheinPlatformAPI.updatePreferences(body),
    prefKey: RRP_AUTO_BIND_PREF_KEY,
    shopId,
    rules,
    normalize: normalizeRrpAutoBindRule,
  });

  const handleSave = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setSaving(true);
    try {
      await persistRules();
      showToast(t('rrpOps.saved'), 'success');
    } catch (e) {
      showToast(e?.message || t('rrpOps.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setRunning(true);
    try {
      await persistRules();
      const enabled = rules.filter((r) => r && r.enabled !== false);
      const summary = await sheinPlatformAPI.autoSubmitRecommendRetailPrice(shopId, {
        scopes: enabled.map((r) => ({
          categoryId: r.categoryId,
          productTypeId: r.productTypeId || '',
        })),
      });
      const ok = Number(summary?.ok) || 0;
      const skip = Number(summary?.skip) || 0;
      const fail = Number(summary?.fail) || 0;
      const errors = Array.isArray(summary?.errors) ? summary.errors : [];
      const parts = [];
      if (ok) parts.push(t('rrpOps.okCount', { count: ok }));
      if (skip) parts.push(t('rrpOps.skipCount', { count: skip }));
      if (fail) parts.push(t('rrpOps.failCount', { count: fail }));
      if (errors.length && !ok && !fail) {
        showToast(errors[0], 'warning');
      } else if (fail && errors.length) {
        showToast(`${parts.join('，')}。${errors[0]}`, 'warning');
      } else if (ok) {
        showToast(t('rrpOps.autoDone', { detail: parts.join('，') }), 'success');
      } else {
        showToast(parts.length ? parts.join('，') : t('rrpOps.nothingToRun'), 'info');
      }
      onRan?.(summary);
    } catch (e) {
      showToast(e?.message || t('rrpOps.autoFailed'), 'error');
    } finally {
      setRunning(false);
    }
  };

  if (!open) return null;
  const disabled = busy || saving || running || loading;

  return createPortal(
    <div className="spo-batch-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        disabled={disabled}
        onClick={() => !disabled && onClose?.()}
      />
      <div
        className="spo-batch-modal__panel spo-discuss-auto-rule-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-rrp-auto-rule-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-rrp-auto-rule-title">{t('rrpOps.sheinRuleTitle')}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled={disabled} onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>

        <div className="spo-batch-modal__body">
          <p className="spo-batch-modal__hint">
            {t('rrpOps.ruleIntroShein')}
          </p>

          <div className="spo-discuss-auto-rule-form">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{t('rrpLib.categoryApply')}</span>
              <CategoryCascaderSelect
                categories={categories}
                value={draftCategoryId}
                label={draftCategoryLabel}
                disabled={disabled || metaLoading || !shopId}
                placeholder={metaLoading ? t('productTemplate.categoriesLoading') : t('rrpLib.categorySearch')}
                emptyText={!shopId ? t('returnList.pickShop') : t('productTemplate.noCategories')}
                includeAllOption
                onChange={onCategoryChange}
              />
            </label>
            <div className="spo-template-auto-rule__switch">
              <SheinSwitch
                checked={draftEnabled}
                disabled={disabled}
                ariaLabel={t('rrpOps.enableDraft')}
                onChange={setDraftEnabled}
              />
              <span>{t('rrpOps.enableDefault')}</span>
            </div>
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={disabled}
              onClick={addRule}
            >
              {t('rrpOps.addRule')}
            </button>
          </div>

          <div className="spo-discuss-auto-rule-list">
            <div className="spo-discuss-auto-rule-list__head">{t('rrpOps.configured', { count: rules.length })}</div>
            {loading ? (
              <p className="spo-batch-modal__hint">{t('returnList.loading')}</p>
            ) : rules.length === 0 ? (
              <p className="spo-batch-modal__hint">{t('rrpOps.noRules')}</p>
            ) : (
              <ul className="spo-discuss-auto-rule-list__ul">
                {rules.map((rule) => (
                  <li key={rule.id} className="spo-discuss-auto-rule-list__item">
                    <div className="spo-discuss-auto-rule-list__main">
                      <strong>{rule.categoryId === '*' ? t('productTemplate.allCategories') : (rule.categoryPathLabel || rule.categoryId)}</strong>
                      <span>{t('rrpOps.matchLibrary')}</span>
                    </div>
                    <div className="spo-discuss-auto-rule-list__ops">
                      <SheinSwitch
                        checked={rule.enabled !== false}
                        disabled={disabled}
                        ariaLabel={t('rrpOps.enableRule')}
                        onChange={(next) => toggleRule(rule.id, next)}
                      />
                      <button
                        type="button"
                        className="spo-btn spo-btn--ghost spo-btn--sm"
                        disabled={disabled}
                        onClick={() => removeRule(rule.id)}
                      >
                        {t('returnList.delete')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary" disabled={disabled} onClick={onClose}>
            {t('shop.cancel')}
          </button>
          <button type="button" className="spo-btn spo-btn--secondary" disabled={disabled || !shopId} onClick={handleSave}>
            {saving ? t('tiktokPublish.saving') : t('shop.save')}
          </button>
          <button type="button" className="spo-btn spo-btn--primary" disabled={disabled || !shopId} onClick={handleRun}>
            {running ? t('returnList.submitting') : t('rrpOps.applyNow')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
